'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  getLegacyAccountToken, clearLegacyAccountToken, migrateLegacyAccountSession, logoutAccount,
  fetchMe, claimSession, saveProgress,
  addFavorite, removeFavorite, addToCollection, removeFromCollection,
} from '../lib/account';
import { getSessionId } from '../lib/matching';
import { trackEvent } from '../lib/telemetry';

// Context voor het (optionele) user-account — los van AuthContext (admin).
// Beheert het account-token, favorieten/collectie-state voor hartjes op
// kaarten, en toont badge-toasts wanneer de backend nieuwe badges toekent.
const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [collectionIds, setCollectionIds] = useState(new Set());
  const [badges, setBadges] = useState([]);
  const [matchingStats, setMatchingStats] = useState(null);
  const [passkeyCount, setPasskeyCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [badgeToasts, setBadgeToasts] = useState([]);
  const toastTimers = useRef([]);

  const applyMe = useCallback((me) => {
    setAccount(me.user);
    setFavoriteIds(new Set(me.favoriteIds || []));
    setCollectionIds(new Set(me.collectionIds || []));
    setBadges(me.badges || []);
    setMatchingStats(me.matching || null);
    setPasskeyCount(me.passkeyCount || 0);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe();
      // null = 204: geen sessie-cookie, dus anoniem (geen 401-ruis meer)
      if (!me) {
        setAccount(null);
        return null;
      }
      applyMe(me);
      return me;
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        setAccount(null);
      }
      return null;
    }
  }, [applyMe]);

  useEffect(() => {
    (async () => {
      // One release-cycle bridge from the former localStorage bearer. The
      // value is exchanged for an HttpOnly cookie. Keep it across transient
      // network failures so migration can be retried on the next page load.
      const legacyToken = getLegacyAccountToken();
      if (legacyToken) {
        try {
          await migrateLegacyAccountSession(legacyToken);
          clearLegacyAccountToken();
        } catch (error) {
          if (error.status === 401 || error.status === 403) clearLegacyAccountToken();
        }
      }
      await refresh();
      setLoading(false);
    })();
    return () => toastTimers.current.forEach(clearTimeout);
  }, [refresh]);

  // Houd de anonieme matching-sessie van dit apparaat gekoppeld aan het
  // account — dekt ook sessies die pas ná het inloggen zijn aangemaakt
  // (bijv. eerste bezoek aan matchparts op een nieuw apparaat)
  const claimedRef = useRef(false);
  useEffect(() => {
    if (!account || claimedRef.current) return;
    claimedRef.current = true;
    claimSession(getSessionId()).catch(() => {});
  }, [account]);

  // Upload bestaande localStorage-check-voortgang naar het account. Dekt
  // voortgang die vóór het inloggen (anoniem) is opgebouwd: die verscheen
  // anders pas op de accountpagina na een herbezoek aan elke setpagina.
  // De server doet last-write-wins op updatedAt, dus oude lokale data
  // overschrijft nooit nieuwere servervoortgang.
  const progressUploadedRef = useRef(false);
  useEffect(() => {
    if (!account || progressUploadedRef.current) return;
    progressUploadedRef.current = true;
    (async () => {
      try {
        const prefix = 'bricksperbag_progress_';
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!key?.startsWith(prefix)) continue;
          const setId = key.slice(prefix.length);
          try {
            const summary = JSON.parse(localStorage.getItem(key) || 'null');
            const map = JSON.parse(localStorage.getItem(`bricksperbag_check_${setId}`) || 'null');
            if (!summary || !map || Object.keys(map).length === 0) continue;
            await saveProgress(setId, {
              progress: map,
              checked: summary.checked || 0,
              total: summary.total || 0,
              updatedAt: summary.updatedAt || Date.now(),
            });
          } catch (e) { /* corrupte of losse entry overslaan */ }
        }
      } catch (e) { /* best effort — volgende paginalading probeert opnieuw */ }
    })();
  }, [account]);

  // Toon toast(s) voor nieuw behaalde badges (backend geeft ze terug bij
  // progress-saves, collectie-wijzigingen en sessie-claims)
  const showNewBadges = useCallback((newBadges) => {
    if (!newBadges?.length) return;
    setBadgeToasts((prev) => [...prev, ...newBadges]);
    toastTimers.current.push(setTimeout(() => {
      setBadgeToasts((prev) => prev.slice(newBadges.length));
    }, 6000));
  }, []);

  // Na een geslaagde login (magic link of passkey): token opslaan, anonieme
  // matching-sessie van dit apparaat claimen, en profiel laden
  const completeLogin = useCallback(async ({ user }) => {
    setAccount(user);
    try {
      const result = await claimSession(getSessionId());
      showNewBadges(result.newBadges);
    } catch {
      // claim is best-effort; login blijft geldig
    }
    await refresh();
  }, [refresh, showNewBadges]);

  const logout = useCallback(async ({ localOnly = false } = {}) => {
    if (!localOnly) await logoutAccount();
    setAccount(null);
    setFavoriteIds(new Set());
    setCollectionIds(new Set());
    setBadges([]);
    setMatchingStats(null);
    setPasskeyCount(0);
  }, []);

  // Optimistische toggle; geeft false terug als er geen account is zodat de
  // caller naar de loginpagina kan sturen
  const toggleFavorite = useCallback(async (setId) => {
    if (!account) {
      trackEvent('Favorite Login Required');
      return false;
    }
    const isFav = favoriteIds.has(setId);
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(setId); else next.add(setId);
      return next;
    });
    try {
      if (isFav) await removeFavorite(setId);
      else await addFavorite(setId);
      trackEvent('Favorite Update Succeeded', { action: isFav ? 'remove' : 'add' });
    } catch {
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(setId); else next.delete(setId);
        return next;
      });
      trackEvent('Favorite Update Failed', { action: isFav ? 'remove' : 'add' });
    }
    return true;
  }, [account, favoriteIds]);

  const toggleCollection = useCallback(async (setId) => {
    if (!account) {
      trackEvent('Collection Login Required');
      return false;
    }
    const inCol = collectionIds.has(setId);
    setCollectionIds((prev) => {
      const next = new Set(prev);
      if (inCol) next.delete(setId); else next.add(setId);
      return next;
    });
    try {
      if (inCol) {
        await removeFromCollection(setId);
      } else {
        const result = await addToCollection(setId);
        showNewBadges(result.newBadges);
      }
      trackEvent('Collection Update Succeeded', { action: inCol ? 'remove' : 'add' });
    } catch {
      setCollectionIds((prev) => {
        const next = new Set(prev);
        if (inCol) next.add(setId); else next.delete(setId);
        return next;
      });
      trackEvent('Collection Update Failed', { action: inCol ? 'remove' : 'add' });
    }
    return true;
  }, [account, collectionIds, showNewBadges]);

  const value = {
    account,
    loading,
    favoriteIds,
    collectionIds,
    badges,
    matchingStats,
    passkeyCount,
    refresh,
    completeLogin,
    logout,
    toggleFavorite,
    toggleCollection,
    showNewBadges,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
      {badgeToasts.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 10000,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {badgeToasts.map((badge, i) => (
            <div key={`${badge.id}-${i}`} style={{
              background: '#fff', border: '2px solid #fab005', borderRadius: 12,
              padding: '10px 16px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              display: 'flex', alignItems: 'center', gap: 10, maxWidth: 320,
            }}>
              <span style={{ fontSize: 28 }}>{badge.emoji}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Badge earned: {badge.name}</div>
                <div style={{ fontSize: 12, color: '#666' }}>{badge.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
